/*
 * Authors: mariushirjanu, Jakub, Koen
 * Created: 13/01/2022
 */
import * as _ from 'underscore';
import { AfterContentChecked, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { CatalogSyncDataService } from '../../order/catalog/catalogsync-data.service';
import { FormattingService } from '../../../shared/services/formatting.service';
import { OrderCatalogService } from '../../order/catalog/order-catalog.service';
import { ICatalogProduct, IProductSearchList, OrderCatalogDataService } from '../../order/catalog/order-catalog-data.service';
import { OrderEntryWebshopDataService } from '../../order/entry/webshop/order-entry-webshop-data.service';
import { WebshopService } from '../webshop.service';
import { ICountry } from '../../../../type_definitions/datatypes';
import { GaFormlyService, IExtraFieldConfigurationObject } from '../../../shared/formly/ga-formly.service';
import { GaMessageService } from '../../../shared/message/ga-message.service';
import { UntypedFormGroup } from '@angular/forms';
import { ICatalogGuideCodePart, WebshopGuideDataService } from './webshop-guide-data.service';
import { CatalogGuideDataService } from '../../order/catalog/guide/catalog-guide-data.service';
import { IDualMultiSelectElement } from '../../../shared/dual-multi-select/dual-multi-select.component';

const BUSY_CHECK_INTERVAL:number = 10000;

export interface IGuideManagementModel {
  selectedCode:string;
  country:ICountry;
}

@Component({
  selector: 'webshop-guide',
  template: `
      <div class="container">
        <formly [form]="form" [fields]="fields" [model]="model" [noValidate]="true"></formly>

        <h2 *ngIf="loading && showGuideSection">
          <activity-indicator toLoad="products"></activity-indicator>
        </h2>

        <div *ngIf="!loading && showGuideSection">
          <div class="d-block">
            <h3>
              <activity-indicator *ngIf="loadingMultiSelect" toLoad="WEBSHOP.TABS.GUIDE"></activity-indicator>
              <span *ngIf="!loadingMultiSelect" translate>WEBSHOP.TABS.GUIDE</span>
            </h3>

            <dual-multi-select [possibleValues]="possibleValues" [(currentValues)]="currentValues" (onChange)="onProductListChange($event)" [selectDisabled]="multiSelectDisabled"></dual-multi-select>
          </div>
          <div class="d-block overflow-hidden mt-3 mb-1">
            <div class="pull-right">
              <button [busyButton]="saving" class="btn btn-primary" (click)="save()" [disabled]="!hasChanges">Save configuration</button>
            </div>
          </div>
        </div>

        <div class="d-block double-space-top">
          <div class="alert alert-warning">
            <div class="pull-left sync-warning-text">
              <i class="fa fa-warning"></i>
              Catalog synchronization is only needed after all configuration changes have been saved.
            </div>
            <div class="pull-right">
              <button [busyButton]="syncing" class="btn btn-warning" (click)="sync()">Synchronize changes with catalog</button>
            </div>
            <div class="clearfix"></div>
          </div>
        </div>
      </div>
    `,
  styleUrls: [ '../guide/webshop-guide.scss' ]
})
export class WebshopGuideComponent implements OnInit, AfterContentChecked, OnDestroy {
  public currentValues:IDualMultiSelectElement[];
  public possibleValues:IDualMultiSelectElement[];

  public syncing:boolean = false;
  public saving:boolean = false;
  public hasChanges:boolean = false;
  public loading:boolean = true;
  public multiSelectDisabled:boolean = true;
  public loadingMultiSelect:boolean = true;
  public showGuideSection:boolean = true;

  private parts:IDualMultiSelectElement[];
  private webshopId:number;
  private countryField:IExtraFieldConfigurationObject;
  private codeField:IExtraFieldConfigurationObject;

  public form:UntypedFormGroup = new UntypedFormGroup({});
  public fields:IExtraFieldConfigurationObject[];

  public model:IGuideManagementModel = {
    selectedCode: undefined,
    country: undefined
  };

  private syncStatusTimer:NodeJS.Timeout;

  constructor(
    private catalogGuideDataService:CatalogGuideDataService,
    private webshopGuideDataService:WebshopGuideDataService,
    private catalogSyncDataService:CatalogSyncDataService,
    private formattingService:FormattingService,
    private formlyService:GaFormlyService,
    private orderCatalogService:OrderCatalogService,
    private orderCatalogDataService:OrderCatalogDataService,
    private messageService:GaMessageService,
    private orderEntryWebshopDataService:OrderEntryWebshopDataService,
    private webshopService:WebshopService,
    private changeDetectorRef:ChangeDetectorRef
  ) {/***/
  }

  public ngOnInit():void {
    //get webshop id
    this.webshopId = this.webshopService.getWebshopId();

    //init catalog service
    this.orderCatalogService.webshopId = this.webshopId;
    this.orderCatalogDataService.start();

    this.fields = this.initFields();
    this.fillFields();

    this.startSyncStatusTimer();
  }

  public ngAfterContentChecked():void {
    this.changeDetectorRef.detectChanges();
  }

  private initFields():IExtraFieldConfigurationObject[] {
    this.countryField = this.formlyService.aSelect('country', 'Country')
      .isDisabled()
      .isLoading()
      .withFormat(this.formattingService.formatCountry)
      .onChange(this.onCountryChange)
      .withLabelClass('col-md-1')
      .withValueClass('col-md-5')
      .build();

    this.codeField = this.formlyService.aSelect('selectedCode', 'Code')
      .onChange(this.onCodeChange)
      .withLabelClass('col-md-1')
      .withValueClass('col-md-5')
      .build();

    return [
      this.countryField,
      this.codeField
    ];
  }

  private fillFields():void {
    this.formlyService.fillField(this.countryField, this.orderEntryWebshopDataService.countries(this.webshopId))
      .then((countries:ICountry[]) => {
        // preselect first country
        this.form.patchValue({ country: countries[ 0 ] });
      });

    this.formlyService.fillField(this.codeField, this.catalogGuideDataService.getCodes(this.webshopId))
      .then((codes:string[]) => {
        if (codes.length) {
          this.codeField.formControl.setValue(codes[0]);
        }
      });
  }

  private loadProducts():void {
    this.loading = true;
    this.orderCatalogDataService.searchAll()
      .then((productSearchList:IProductSearchList) => {
        const products:ICatalogGuideCodePart[] = <ICatalogGuideCodePart[]>productSearchList.products;
        this.parts = this.elements(products, 'part', 'shortDescription');
      }, () => {
        this.parts = [];
      })
      .finally(() => {
        this.getAssignedParts();
        this.loading = false;
      });
  }

  public save():Promise<void> {
    this.saving = true;
    return this.webshopGuideDataService.saveCodeParts(this.webshopId, this.model.country?.id, this.model.selectedCode, _.pluck(this.currentValues, 'id'))
      .then((parts:ICatalogGuideCodePart[]) => {
        this.handleAssignedParts(parts);
      })
      .catch(() => {
        this.currentValues = [];
      })
      .finally(() => {
        this.computePossibilities();
        this.hasChanges = false;
        this.saving = false;
      });
  }

  public onProductListChange(hasChanges:boolean):void {
    this.hasChanges = hasChanges;
  }

  private computePossibilities():void {
    this.possibleValues = _.filter(this.parts, value => !_.findWhere(this.currentValues, { id: value.id }));
  }

  private reset():void {
    this.showGuideSection = false;
    this.currentValues = [];
    this.computePossibilities();
  }

  private element(product:ICatalogProduct | ICatalogGuideCodePart, id:string, name:string):IDualMultiSelectElement {
    return {
      id: product[ id ],
      name: `${product[ id ]} (${product[ name ]})`
    };
  }

  private elements(products:(ICatalogProduct | ICatalogGuideCodePart)[], id:string, name:string):IDualMultiSelectElement[] {
    return _.map(products, (product:ICatalogProduct | ICatalogGuideCodePart) => {
      return this.element(product, id, name);
    });
  }

  private getAssignedParts():void {
    if (this.model.country && this.model.selectedCode) {
      this.showGuideSection = true;
      this.multiSelectDisabled = true;
      this.loadingMultiSelect = true;
      this.webshopGuideDataService.getCodeParts(this.webshopId, this.model.country.id, this.model.selectedCode)
        .then((parts:ICatalogGuideCodePart[]) => {
          this.handleAssignedParts(parts);
        })
        .catch(() => {
          this.currentValues = [];
        })
        .finally(() => {
          this.computePossibilities();
          this.multiSelectDisabled = false;
          this.loadingMultiSelect = false;
        });
    } else {
      this.loadingMultiSelect = false;
    }
  }

  private handleAssignedParts(parts:ICatalogGuideCodePart[]):void {
    const assigned:IDualMultiSelectElement[] = this.elements(parts, 'part', 'buyingGuideCode');
    this.currentValues = [];
    _.each(assigned, (part:IDualMultiSelectElement) => {
      const existing:IDualMultiSelectElement = _.findWhere(this.parts, { id: String(part.id) });
      if (existing) {
        this.currentValues.push({
          id: part.id,
          name: existing.name
        });
      }
    });
  }

  private onCodeChange = (newCode:string, oldCode:string) => {
    const loadParts = () => {
      if (newCode) {
        this.getAssignedParts();
      } else {
        this.reset();
      }
    };

    if (this.hasChanges) {
      this.model.selectedCode = oldCode;
      this.messageService.showEditingWarningDialog(() => {
        this.model.selectedCode = newCode;
        loadParts();
      });
    } else {
      loadParts();
    }
  };

  private onCountryChange = (newCountry:ICountry, oldCountry:ICountry) => {
    const loadProducts = () => {
      if (newCountry) {
        this.orderCatalogService.countryId = newCountry.id;
        this.loadProducts();
      } else {
        this.reset();
      }
    };

    if (this.hasChanges) {
      this.model.country = oldCountry;
      this.messageService.showEditingWarningDialog(() => {
        this.model.country = newCountry;
        loadProducts();
      });
    } else {
      loadProducts();
    }
  };

  public sync():void {
    this.cancelSyncStatusTimer();
    this.syncing = true;
    this.catalogSyncDataService.sync(this.webshopId)
      .then(this.startSyncStatusTimer);
  }

  private startSyncStatusTimer = ():void => {
    const checkStatus = () => {
      this.catalogSyncDataService.syncBusy(this.webshopId)
        .then((busy:boolean) => {
          this.syncing = busy;
          //reschedule
          this.syncStatusTimer = setTimeout(checkStatus, BUSY_CHECK_INTERVAL, false);
        });
    };

    checkStatus();
  };

  private cancelSyncStatusTimer():void {
    clearTimeout(this.syncStatusTimer);
    this.catalogSyncDataService.cancelSyncBusyRequests();
  }

  public ngOnDestroy():void {
    this.orderCatalogDataService.clear();
    this.cancelSyncStatusTimer();
  }
}